import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    Store, Search, Eye, EyeOff, Trash2, ExternalLink, Package,
    AlertTriangle, X, Loader, Filter, RefreshCw
} from 'lucide-react';
import { isAdminUser } from '../utils/adminClaim';

const AdminStoreManagement = ({ userInfo }) => {
    const navigate = useNavigate();
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [selectedStore, setSelectedStore] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);

    const loadStores = useCallback(async () => {
        try {
            setLoading(true);
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('businessType', '==', 'seller'));
            const snapshot = await getDocs(q);
            
            const storeList = await Promise.all(
                snapshot.docs.map(async (docSnap) => {
                    const data = { id: docSnap.id, ...docSnap.data() };
                    const productsRef = collection(db, 'marketplaceProducts');
                    const productsQuery = query(productsRef, where('sellerId', '==', docSnap.id));
                    const productsSnapshot = await getDocs(productsQuery);
                    
                    return {
                        ...data,
                        productCount: productsSnapshot.size,
                        storeName: data.storeName || data.businessName || data.companyName || '이름 없음',
                        storeVisible: data.storeVisible ?? true,
                    };
                })
            );
            storeList.sort((a, b) => {
                const dateA = a.createdAt?.toDate?.() || new Date(0);
                const dateB = b.createdAt?.toDate?.() || new Date(0);
                return dateB - dateA;
            });
            setStores(storeList);
        } catch (error) {
            console.error('스토어 로드 실패:', error);
            alert('스토어 목록을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!userInfo) {
            alert('로그인이 필요합니다.');
            navigate('/login');
            return;
        }
        const isAdmin = isAdminUser(userInfo);
        if (!isAdmin) {
            alert('관리자만 접근 가능합니다.');
            navigate('/dashboard');
            return;
        }
        loadStores();
    }, [userInfo, navigate, loadStores]);

    const toggleVisibility = async (store) => {
        try {
            setActionLoading(store.id);
            const newVisibility = !store.storeVisible;
            await updateDoc(doc(db, 'users', store.id), {
                storeVisible: newVisibility,
                storeUpdatedAt: serverTimestamp(),
            });
            setStores(prev => prev.map(s => 
                s.id === store.id ? { ...s, storeVisible: newVisibility } : s
            ));
            alert(store.storeName + ' 스토어가 ' + (newVisibility ? '공개' : '비공개') + '되었습니다.');
        } catch (error) {
            console.error('상태 변경 실패:', error);
            alert('상태 변경에 실패했습니다.');
        } finally {
            setActionLoading(null);
        }
    };

    const deleteStore = async () => {
        if (!selectedStore) return;
        try {
            setActionLoading(selectedStore.id);
            await updateDoc(doc(db, 'users', selectedStore.id), {
                sellerStatus: 'revoked',
                storeVisible: false,
                revokedAt: serverTimestamp(),
                revokedBy: userInfo.uid,
            });
            const productsRef = collection(db, 'marketplaceProducts');
            const productsQuery = query(productsRef, where('sellerId', '==', selectedStore.id));
            const productsSnapshot = await getDocs(productsQuery);
            const updatePromises = productsSnapshot.docs.map(productDoc =>
                updateDoc(doc(db, 'marketplaceProducts', productDoc.id), {
                    status: 'suspended',
                    suspendedAt: serverTimestamp(),
                    suspendedBy: userInfo.uid,
                })
            );
            await Promise.all(updatePromises);
            setStores(prev => prev.filter(s => s.id !== selectedStore.id));
            setShowDeleteModal(false);
            setSelectedStore(null);
            alert(selectedStore.storeName + ' 스토어의 판매자 승인이 취소되었습니다.');
        } catch (error) {
            console.error('스토어 삭제 실패:', error);
            alert('스토어 삭제에 실패했습니다.');
        } finally {
            setActionLoading(null);
        }
    };

    const filteredStores = stores.filter(store => {
        const matchesSearch = !searchQuery ||
            store.storeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            store.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            store.businessNumber?.includes(searchQuery);
        const matchesFilter = filterStatus === 'all' ||
            (filterStatus === 'visible' && store.storeVisible) ||
            (filterStatus === 'hidden' && !store.storeVisible);
        const isApproved = store.sellerStatus === 'approved';
        return matchesSearch && matchesFilter && isApproved;
    });

    const goToStore = (storeId) => {
        window.open('/marketplace/seller/' + storeId, '_blank');
    };

    const getStatusBadge = (store) => {
        if (store.sellerStatus !== 'approved') {
            return <span style={styles.badgeRevoked}>승인취소</span>;
        }
        if (store.storeVisible) {
            return <span style={styles.badgeVisible}>공개</span>;
        }
        return <span style={styles.badgeHidden}>비공개</span>;
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <Loader size={40} style={{ animation: 'spin 1s linear infinite' }} />
                <p>스토어 목록을 불러오는 중...</p>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}><Store size={28} /> 스토어 관리</h1>
                    <p style={styles.subtitle}>마켓플레이스 스토어를 관리합니다 (총 {stores.filter(s => s.sellerStatus === 'approved').length}개)</p>
                </div>
                <button style={styles.refreshBtn} onClick={loadStores}><RefreshCw size={18} /> 새로고침</button>
            </div>

            <div style={styles.filterSection}>
                <div style={styles.searchBox}>
                    <Search size={20} color="#64748b" />
                    <input type="text" placeholder="스토어명, 이메일, 사업자번호로 검색..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={styles.searchInput} />
                </div>
                <div style={styles.filterGroup}>
                    <Filter size={18} color="#64748b" />
                    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={styles.filterSelect}>
                        <option value="all">전체</option>
                        <option value="visible">공개 스토어</option>
                        <option value="hidden">비공개 스토어</option>
                    </select>
                </div>
            </div>

            <div style={styles.statsRow}>
                <div style={styles.statCard}>
                    <div style={styles.statIcon}><Store size={24} color="#6366f1" /></div>
                    <div><div style={styles.statValue}>{stores.filter(s => s.sellerStatus === 'approved').length}</div><div style={styles.statLabel}>전체 스토어</div></div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statIcon, backgroundColor: '#d1fae5'}}><Eye size={24} color="#10b981" /></div>
                    <div><div style={styles.statValue}>{stores.filter(s => s.storeVisible && s.sellerStatus === 'approved').length}</div><div style={styles.statLabel}>공개 스토어</div></div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statIcon, backgroundColor: '#fee2e2'}}><EyeOff size={24} color="#ef4444" /></div>
                    <div><div style={styles.statValue}>{stores.filter(s => !s.storeVisible && s.sellerStatus === 'approved').length}</div><div style={styles.statLabel}>비공개 스토어</div></div>
                </div>
            </div>

            <div style={styles.tableContainer}>
                {filteredStores.length === 0 ? (
                    <div style={styles.emptyState}><Store size={48} color="#cbd5e1" /><p>등록된 스토어가 없습니다</p></div>
                ) : (
                    <table style={styles.table}>
                        <thead><tr><th style={styles.th}>스토어</th><th style={styles.th}>연락처</th><th style={styles.th}>상품</th><th style={styles.th}>상태</th><th style={styles.th}>관리</th></tr></thead>
                        <tbody>
                            {filteredStores.map(store => (
                                <tr key={store.id} style={styles.tr}>
                                    <td style={styles.td}>
                                        <div style={styles.storeInfo}>
                                            <div style={styles.storeLogo}>{store.storeLogo || store.logoUrl ? <img src={store.storeLogo || store.logoUrl} alt={store.storeName} style={styles.logoImage} /> : <Store size={24} color="#94a3b8" />}</div>
                                            <div><div style={styles.storeName}>{store.storeName}</div>{store.businessNumber && <div style={styles.businessNumber}>{store.businessNumber}</div>}</div>
                                        </div>
                                    </td>
                                    <td style={styles.td}><div style={styles.contactInfo}><div>{store.storeEmail || store.email || '-'}</div><div style={styles.phone}>{store.storePhone || store.phone || '-'}</div></div></td>
                                    <td style={styles.td}><div style={styles.productCount}><Package size={16} color="#64748b" />{store.productCount}개</div></td>
                                    <td style={styles.td}>{getStatusBadge(store)}</td>
                                    <td style={styles.td}>
                                        <div style={styles.actionButtons}>
                                            <button style={styles.actionBtn} onClick={() => goToStore(store.id)} title="스토어 보기"><ExternalLink size={16} /></button>
                                            <button style={{...styles.actionBtn, backgroundColor: store.storeVisible ? '#fee2e2' : '#d1fae5', color: store.storeVisible ? '#dc2626' : '#059669'}} onClick={() => toggleVisibility(store)} disabled={actionLoading === store.id} title={store.storeVisible ? '비공개로 전환' : '공개로 전환'}>{actionLoading === store.id ? <Loader size={16} style={{animation:'spin 1s linear infinite'}} /> : store.storeVisible ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                                            <button style={{...styles.actionBtn, backgroundColor: '#fee2e2', color: '#dc2626'}} onClick={() => { setSelectedStore(store); setShowDeleteModal(true); }} title="판매자 승인 취소"><Trash2 size={16} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {showDeleteModal && selectedStore && (
                <div style={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
                    <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <div style={styles.modalIcon}><AlertTriangle size={48} color="#dc2626" /></div>
                        <h3 style={styles.modalTitle}>판매자 승인 취소</h3>
                        <p style={styles.modalText}><strong>{selectedStore.storeName}</strong> 스토어의 판매자 승인을 취소하시겠습니까?</p>
                        <div style={styles.modalWarning}><AlertTriangle size={16} /><span>이 작업은 되돌릴 수 없습니다. 해당 판매자의 모든 상품이 비활성화됩니다.</span></div>
                        <div style={styles.modalInfo}><div>상품 수: {selectedStore.productCount}개</div><div>이메일: {selectedStore.email || '-'}</div></div>
                        <div style={styles.modalButtons}>
                            <button style={styles.cancelBtn} onClick={() => setShowDeleteModal(false)}><X size={18} /> 취소</button>
                            <button style={styles.deleteBtn} onClick={deleteStore} disabled={actionLoading === selectedStore.id}>{actionLoading === selectedStore.id ? <Loader size={18} style={{animation:'spin 1s linear infinite'}} /> : <Trash2 size={18} />} 승인 취소</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: { padding: '24px', maxWidth: '1400px', margin: '0 auto' },
    loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '400px', gap: '16px', color: '#64748b' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
    title: { display: 'flex', alignItems: 'center', gap: '12px', fontSize: '28px', fontWeight: '700', color: '#1e293b', margin: 0 },
    subtitle: { fontSize: '14px', color: '#64748b', margin: '8px 0 0 0' },
    refreshBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    filterSection: { display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' },
    searchBox: { flex: 1, minWidth: '300px', display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '10px' },
    searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: '14px' },
    filterGroup: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px', backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '10px' },
    filterSelect: { border: 'none', outline: 'none', fontSize: '14px', backgroundColor: 'transparent', cursor: 'pointer' },
    statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
    statCard: { display: 'flex', alignItems: 'center', gap: '16px', padding: '20px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    statIcon: { width: '48px', height: '48px', borderRadius: '12px', backgroundColor: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    statValue: { fontSize: '24px', fontWeight: '700', color: '#1e293b' },
    statLabel: { fontSize: '13px', color: '#64748b' },
    tableContainer: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
    tr: { borderBottom: '1px solid #f1f5f9' },
    td: { padding: '16px', fontSize: '14px', color: '#1e293b', verticalAlign: 'middle' },
    storeInfo: { display: 'flex', alignItems: 'center', gap: '12px' },
    storeLogo: { width: '48px', height: '48px', borderRadius: '8px', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
    logoImage: { width: '100%', height: '100%', objectFit: 'cover' },
    storeName: { fontWeight: '600', color: '#1e293b' },
    businessNumber: { fontSize: '12px', color: '#94a3b8', fontFamily: 'monospace', marginTop: '2px' },
    contactInfo: { fontSize: '13px' },
    phone: { color: '#64748b', marginTop: '2px' },
    productCount: { display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b' },
    badgeVisible: { display: 'inline-block', padding: '4px 12px', backgroundColor: '#d1fae5', color: '#059669', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
    badgeHidden: { display: 'inline-block', padding: '4px 12px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
    badgeRevoked: { display: 'inline-block', padding: '4px 12px', backgroundColor: '#fef3c7', color: '#92400e', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
    actionButtons: { display: 'flex', gap: '8px' },
    actionBtn: { width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f1f5f9', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#475569' },
    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', color: '#94a3b8', gap: '12px' },
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: 'white', borderRadius: '16px', padding: '32px', maxWidth: '450px', width: '90%', textAlign: 'center' },
    modalIcon: { marginBottom: '16px' },
    modalTitle: { fontSize: '20px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' },
    modalText: { fontSize: '15px', color: '#64748b', marginBottom: '16px', lineHeight: '1.6' },
    modalWarning: { display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 16px', backgroundColor: '#fef3c7', borderRadius: '8px', fontSize: '13px', color: '#92400e', textAlign: 'left', marginBottom: '16px' },
    modalInfo: { padding: '12px 16px', backgroundColor: '#f8fafc', borderRadius: '8px', fontSize: '13px', color: '#64748b', marginBottom: '24px', textAlign: 'left' },
    modalButtons: { display: 'flex', gap: '12px', justifyContent: 'center' },
    cancelBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', backgroundColor: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    deleteBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
};

export default AdminStoreManagement;
